import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useSocket } from "@/hooks/useSocket";
import { useAuth, API, authedHeaders } from "@/hooks/useAuth";
import { RULESETS, STATUS } from "@/game/rulesets";
import { AdSlot } from "@/components/ads/AdSlot";
import { Send, Swords, Circle, Trophy, AtSign, Flag } from "lucide-react";
import { toast } from "sonner";

const PLAYABLE = RULESETS.filter((r) => r.status === STATUS.PLAYABLE);

const BAND_LABEL = { under_13: "Kids", teen: "Teens", adult: "Adults" };

export function Lobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready, presence, send, subscribe } = useSocket();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [rulesetId, setRulesetId] = useState("zimbabwe-standard");
  const [pendingInvites, setPendingInvites] = useState([]); // received
  const [sentInviteTo, setSentInviteTo] = useState(null);
  const [handle, setHandle] = useState("");
  const [handleBusy, setHandleBusy] = useState(false);
  const [cannedPhrases, setCannedPhrases] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  const isKid = user?.age_band === "under_13";

  useEffect(() => {
    if (!isKid) return;
    axios.get(`${API}/auth/canned-phrases`).then((r) => setCannedPhrases(r.data.phrases || [])).catch(() => {});
  }, [isKid]);

  useEffect(() => {
    axios.get(`${API}/lobby/announcements`).then((r) => setAnnouncements(r.data.announcements || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = subscribe((s) => {
      const m = s.message;
      if (!m) return;
      if (m.type === "CHAT" && m.room === "lobby") {
        setMessages((prev) => [...prev.slice(-49), m]);
      } else if (m.type === "CHAT_BLOCKED") {
        const map = {
          age_not_verified: "Verify your age to chat.",
          kids_only_canned: "Kids zone — please tap a canned phrase instead.",
          phrase_not_allowed: "That phrase isn't on the kids-safe list.",
          muted: "You're muted right now. Try again later.",
          abusive_language: `Message blocked for abusive content${m.strikes ? ` (strike ${m.strikes}/5)` : ""}. ${m.auto_action === "muted_24h" ? "You're now muted for 24 hours." : m.auto_action === "suspended" ? "Your account has been suspended." : ""}`,
        };
        toast.error(map[m.reason] || "Message blocked.");
      } else if (m.type === "INVITE_RECEIVED") {
        setPendingInvites((prev) => [...prev, m]);
        toast.message(`${m.from_username} invites you to play ${m.ruleset_id}`);
      } else if (m.type === "INVITE_DECLINED") {
        toast.error("Invite declined.");
        setSentInviteTo(null);
      } else if (m.type === "INVITE_REJECTED") {
        toast.error(m.reason || "Invite rejected.");
        setSentInviteTo(null);
      } else if (m.type === "INVITE_EXPIRED") {
        toast.error("Invite expired.");
        setSentInviteTo(null);
        setPendingInvites((prev) => prev.filter((p) => p.invite_id !== m.invite_id));
      } else if (m.type === "GAME_START") {
        const opponent = m.a.user_id === user?.id ? m.b : m.a;
        const youAre = m.a.user_id === user?.id ? "A" : "B";
        navigate(`/online/${m.game_id}?ruleset=${m.ruleset_id}&side=${youAre}&opp=${opponent.user_id}&opp_name=${encodeURIComponent(opponent.username || "Opponent")}`);
      }
    });
    return unsub;
  }, [subscribe, user?.id, navigate]);

  // Hard gate: must be age-verified to be in the lobby at all.
  // Must come AFTER all hook calls so we don't violate the rules-of-hooks.
  if (user && !user.age_verified) {
    return <Navigate to="/verify-age" replace />;
  }

  const sendChat = (e, presetText) => {
    e?.preventDefault?.();
    const text = (presetText ?? draft).trim();
    if (!text) return;
    const kind = isKid || presetText ? "canned" : "text";
    if (send({ type: "CHAT", room: "lobby", text, kind })) {
      if (!presetText) setDraft("");
    }
  };

  const challenge = (target) => {
    if (target.in_game) return toast.error(`${target.username} is mid-game.`);
    if (target.user_id === user?.id) return toast.error("You can't challenge yourself.");
    send({ type: "INVITE_SEND", to: target.user_id, ruleset_id: rulesetId });
    setSentInviteTo(target.user_id);
    toast.message(`Invite sent to ${target.username}.`);
  };

  const challengeByHandle = async (e) => {
    e?.preventDefault?.();
    if (!handle.trim()) return;
    setHandleBusy(true);
    try {
      const { data } = await axios.post(`${API}/lobby/lookup`, { handle: handle.trim() }, { headers: authedHeaders() });
      if (!data.online) {
        toast.error(`${data.username} is offline right now. They need to be online to play.`);
        return;
      }
      if (data.in_game) {
        toast.error(`${data.username} is mid-game.`);
        return;
      }
      send({ type: "INVITE_SEND", to: data.user_id, ruleset_id: rulesetId, by_handle: true });
      setSentInviteTo(data.user_id);
      toast.message(`Cross-age invite sent to ${data.username}.`);
      setHandle("");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setHandleBusy(false);
    }
  };

  const respondToInvite = (invite, accept) => {
    send({ type: "INVITE_RESPOND", invite_id: invite.invite_id, accept });
    setPendingInvites((prev) => prev.filter((p) => p.invite_id !== invite.invite_id));
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Online play</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">Lobby</h1>
            <p className="text-foreground/70 mt-2 max-w-xl">
              Challenge another live player to a real-time match. Pick a country
              variant, then tap <strong>Challenge</strong> next to anyone who&apos;s online and free.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user?.age_band && (
              <Badge variant="outline" className="rounded-full" data-testid="age-band-badge">
                {BAND_LABEL[user.age_band] || user.age_band}
              </Badge>
            )}
            <Badge data-testid="lobby-connection" variant={ready ? "default" : "outline"} className="rounded-full">
              <Circle className={`h-2 w-2 mr-1 ${ready ? "fill-emerald-500 text-emerald-500" : "fill-muted text-muted"}`} />
              {ready ? "Connected" : "Connecting…"}
            </Badge>
            <Button variant="outline" onClick={() => navigate("/leaderboard")} className="rounded-full">
              <Trophy className="h-4 w-4 mr-2" /> Leaderboard
            </Button>
          </div>
        </div>

        <div className="mt-6">
          <AdSlot zoneId="lobby-top" size="leaderboard" />
        </div>

        {announcements.length > 0 && (
          <div data-testid="lobby-announcements" className="mt-4 space-y-2">
            {announcements.map((a) => (
              <Card
                key={a.id}
                data-testid={`announcement-${a.id}`}
                className={`p-3 border-l-4 ${
                  a.severity === "critical" ? "border-l-destructive bg-destructive/5"
                  : a.severity === "warning" ? "border-l-amber-500 bg-amber-500/5"
                  : "border-l-primary bg-primary/5"
                }`}
              >
                <p className="text-sm">{a.message}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Challenge a known friend by username/email (cross-band exception). */}
        <Card className="mt-6 p-4">
          <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-display text-lg">Know someone by username?</p>
              <p className="text-xs text-muted-foreground">
                Challenge anyone by exact username or email — works across age
                groups (e.g. an uncle playing his nephew). Both players must
                accept the invite once.
              </p>
            </div>
            <form onSubmit={challengeByHandle} className="flex gap-2">
              <div className="relative">
                <AtSign className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="lobby-handle-input"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="username or email"
                  className="pl-8 w-64"
                />
              </div>
              <Button type="submit" disabled={handleBusy || !handle.trim()} data-testid="lobby-handle-submit" className="rounded-full">
                <Swords className="h-4 w-4 mr-2" /> Challenge
              </Button>
            </form>
          </div>
        </Card>

        {pendingInvites.length > 0 && (
          <div className="mt-6 space-y-2">
            {pendingInvites.map((inv) => (
              <Card key={inv.invite_id} data-testid={`invite-${inv.invite_id}`} className="p-4 flex flex-wrap items-center justify-between gap-3 border-primary/50">
                <p className="text-sm">
                  <strong>{inv.from_username}</strong> (rating {inv.from_rating}) invites you to play{" "}
                  <Badge variant="outline" className="rounded-full">{inv.ruleset_id}</Badge>
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => respondToInvite(inv, true)} data-testid={`accept-${inv.invite_id}`} className="rounded-full">Accept</Button>
                  <Button size="sm" variant="outline" onClick={() => respondToInvite(inv, false)} data-testid={`decline-${inv.invite_id}`} className="rounded-full">Decline</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6 mt-8">
          {/* Online players */}
          <Card className="lg:col-span-5 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl">Online now</h2>
              <Badge variant="outline" className="rounded-full">{presence.length} players</Badge>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Variant</span>
              <Select value={rulesetId} onValueChange={setRulesetId}>
                <SelectTrigger className="rounded-full" data-testid="lobby-variant">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAYABLE.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name} · {r.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {presence.length === 0 ? (
              <p className="text-sm text-muted-foreground">No one else is online right now. Share the link and play a friend!</p>
            ) : (
              <ul className="divide-y divide-border/40" data-testid="online-list">
                {presence.map((p) => {
                  const isSelf = p.user_id === user?.id;
                  return (
                    <li key={p.user_id} className="flex items-center justify-between py-2.5 gap-2">
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <Circle className={`h-2 w-2 fill-emerald-500 text-emerald-500`} />
                          {p.username} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                          {p.in_game && <Badge variant="outline" className="text-[10px] rounded-full">in game</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.country || "—"} · rating {p.rating}
                        </p>
                      </div>
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant={p.in_game ? "ghost" : "default"}
                          disabled={p.in_game || sentInviteTo === p.user_id}
                          onClick={() => challenge(p)}
                          data-testid={`challenge-${p.user_id}`}
                          className="rounded-full"
                        >
                          <Swords className="h-4 w-4 mr-1" />
                          {sentInviteTo === p.user_id ? "Sent" : "Challenge"}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Chat */}
          <Card className="lg:col-span-7 p-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl">Lobby chat</h2>
              {isKid && (
                <Badge variant="outline" className="rounded-full text-[10px]">Kids-safe · canned phrases only</Badge>
              )}
            </div>
            <div data-testid="lobby-chat" className="flex-1 min-h-[280px] max-h-[420px] overflow-y-auto bg-muted/30 rounded-lg p-3 space-y-1.5">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {isKid
                    ? "Tap a phrase below to say hi — chat is for other kids your age."
                    : "Say hi — chat is shared with everyone in your age group."}
                </p>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className="text-sm group flex items-start gap-2">
                    <p className="flex-1">
                      <strong className="text-primary">{m.username}:</strong>{" "}
                      <span className="text-foreground/90">{m.text}</span>
                    </p>
                    {m.from !== user?.id && (
                      <button
                        type="button"
                        onClick={() => {
                          const reason = window.prompt(`Report ${m.username}? Describe the issue briefly (e.g. "abusive language", "spam"):`);
                          if (!reason || !reason.trim()) return;
                          axios.post(`${API}/lobby/report`, {
                            reported_username: m.username, reason: reason.trim(),
                            context: m.text, room: "lobby", message_id: m.message_id,
                          }, { headers: authedHeaders() })
                            .then(() => toast.success("Report submitted."))
                            .catch((e) => toast.error(e.response?.data?.detail || e.message));
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        data-testid={`report-msg-${i}`}
                        title={`Report ${m.username}`}
                      >
                        <Flag className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            {isKid ? (
              <div data-testid="canned-phrases" className="flex flex-wrap gap-2 mt-3">
                {cannedPhrases.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => sendChat(null, p)}
                    className="rounded-full text-xs"
                    data-testid={`canned-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            ) : (
              <form onSubmit={sendChat} className="flex gap-2 mt-3">
                <Input
                  data-testid="chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  maxLength={500}
                />
                <Button type="submit" data-testid="chat-send" className="rounded-full">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            )}
          </Card>
        </div>
      </section>
    </Layout>
  );
}
